/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDeviceIntegrationSchedulesRequest,
  ListDeviceIntegrationSchedulesSecurity,
} from "../models/operations/listdeviceintegrationschedules.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDeviceIntegrationSchedulesQuery,
  DeviceIntegrationSchedulesQueryData,
  prefetchDeviceIntegrationSchedules,
  queryKeyDeviceIntegrationSchedules,
} from "./deviceIntegrationSchedules.core.js";
export {
  buildDeviceIntegrationSchedulesQuery,
  type DeviceIntegrationSchedulesQueryData,
  prefetchDeviceIntegrationSchedules,
  queryKeyDeviceIntegrationSchedules,
};

export type DeviceIntegrationSchedulesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSchedules deviceIntegrations
 *
 * @remarks
 * List the sync schedules and their state for a provider's org-wide device integration config. Returns an empty list when no config is set.
 */
export function useDeviceIntegrationSchedules(
  request: ListDeviceIntegrationSchedulesRequest,
  security?: ListDeviceIntegrationSchedulesSecurity | undefined,
  options?: QueryHookOptions<
    DeviceIntegrationSchedulesQueryData,
    DeviceIntegrationSchedulesQueryError
  >,
): UseQueryResult<
  DeviceIntegrationSchedulesQueryData,
  DeviceIntegrationSchedulesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDeviceIntegrationSchedulesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSchedules deviceIntegrations
 *
 * @remarks
 * List the sync schedules and their state for a provider's org-wide device integration config. Returns an empty list when no config is set.
 */
export function useDeviceIntegrationSchedulesSuspense(
  request: ListDeviceIntegrationSchedulesRequest,
  security?: ListDeviceIntegrationSchedulesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DeviceIntegrationSchedulesQueryData,
    DeviceIntegrationSchedulesQueryError
  >,
): UseSuspenseQueryResult<
  DeviceIntegrationSchedulesQueryData,
  DeviceIntegrationSchedulesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDeviceIntegrationSchedulesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDeviceIntegrationSchedulesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DeviceIntegrationSchedulesQueryData,
): DeviceIntegrationSchedulesQueryData | undefined {
  const key = queryKeyDeviceIntegrationSchedules(...queryKeyBase);

  return client.setQueryData<DeviceIntegrationSchedulesQueryData>(key, data);
}

export function invalidateDeviceIntegrationSchedules(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "listSchedules",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDeviceIntegrationSchedules(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "listSchedules"],
  });
}
